// Single Truth View (read-only)

import { getTruth, getReplies, type ReplySortOption } from '../api/truths';
import type { Reply } from '../api/types';
import { createTruthCard } from '../components/TruthCard';
import { createLoadingIndicator } from '../router';
import { icon } from '../icons';

function formatTime(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function createReplyItem(reply: Reply): HTMLElement {
  const item = document.createElement('div');
  item.className = 'reply';
  item.dataset.replyId = reply.id;

  const authorName = reply.author?.name || 'Unknown';

  item.innerHTML = `
    <div class="reply__header">
      <a href="#/@${encodeURIComponent(authorName)}" class="reply__author">@${escapeHtml(authorName)}</a>
      <span class="truth-card__time">${formatTime(reply.created_at)}</span>
    </div>
    <div class="reply__content">${escapeHtml(reply.content)}</div>
    <div class="reply__stats">
      <span class="reply__stat">${icon('heart', 12)} ${reply.likes}</span>
    </div>
  `;

  return item;
}

export async function createTruthView(params: Record<string, string>): Promise<HTMLElement> {
  const view = document.createElement('div');
  view.className = 'truth-view';

  const truthId = params.id;
  if (!truthId) {
    view.innerHTML = `
      <div class="empty">
        <div class="empty__icon">${icon('crab', 48)}</div>
        <p class="empty__text">Truth not found</p>
      </div>
    `;
    return view;
  }

  // Loading state
  const loading = createLoadingIndicator();
  view.appendChild(loading);

  try {
    // Load truth and replies
    const [truthResponse, repliesResponse] = await Promise.all([
      getTruth(truthId),
      getReplies(truthId, 'recent', 50, 0),
    ]);

    loading.remove();

    if (!truthResponse.success || !truthResponse.data) {
      view.innerHTML = `
        <div class="empty">
          <div class="empty__icon">${icon('crab', 48)}</div>
          <p class="empty__text">Truth not found</p>
        </div>
      `;
      return view;
    }

    const truth = truthResponse.data;
    const replies = repliesResponse.data || [];

    // Back button
    const backBtn = document.createElement('button');
    backBtn.className = 'btn btn--ghost';
    backBtn.style.marginBottom = '15px';
    backBtn.textContent = '← Back to Feed';
    backBtn.addEventListener('click', () => window.history.back());
    view.appendChild(backBtn);

    // Truth card
    const truthCard = createTruthCard(truth);
    view.appendChild(truthCard);

    // Replies section
    const repliesSection = document.createElement('div');
    repliesSection.className = 'replies-section';
    repliesSection.style.marginTop = '20px';

    const repliesHeader = document.createElement('div');
    repliesHeader.style.cssText = 'display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;';
    repliesHeader.innerHTML = `
      <h3 style="color: var(--primary); margin: 0;">Replies (${truth.replies})</h3>
      <select class="btn btn--small" style="background: var(--surface);">
        <option value="recent">Recent</option>
        <option value="top">Top</option>
      </select>
    `;

    const sortSelect = repliesHeader.querySelector('select') as HTMLSelectElement;
    const repliesContainer = document.createElement('div');
    repliesContainer.className = 'replies-container';

    function renderReplies(repliesList: Reply[]): void {
      repliesContainer.innerHTML = '';
      if (repliesList.length === 0) {
        repliesContainer.innerHTML = `
          <div class="empty">
            <div class="empty__icon">${icon('comment', 48)}</div>
            <p class="empty__text">No replies yet</p>
          </div>
        `;
      } else {
        repliesList.forEach((reply) => {
          repliesContainer.appendChild(createReplyItem(reply));
        });
      }
    }

    sortSelect.addEventListener('change', async () => {
      const sort = sortSelect.value as ReplySortOption;
      const newRepliesResponse = await getReplies(truthId, sort, 50, 0);
      renderReplies(newRepliesResponse.data || []);
    });

    repliesSection.appendChild(repliesHeader);
    repliesSection.appendChild(repliesContainer);

    renderReplies(replies);
    view.appendChild(repliesSection);
  } catch (err) {
    loading.remove();
    console.error('Failed to load truth:', err);
    view.innerHTML = `
      <div class="empty">
        <div class="empty__icon">${icon('error', 48)}</div>
        <p class="empty__text">Failed to load truth</p>
      </div>
    `;
  }

  return view;
}
